import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import { EnvelopeSimple, Lock, User, Phone, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { AGB_VERSION } from './legal/Terms';

const RegisterPage = () => {
  const { register, t, language } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(language === 'de' ? 'Passwörter stimmen nicht überein' : 'Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(language === 'de' ? 'Passwort muss mindestens 6 Zeichen haben' : 'Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!agbAccepted) {
      setError(language === 'de' ? 'Bitte akzeptiere die AGB und Datenschutzerklärung.' : 'Please accept the Terms and Privacy Policy.');
      setLoading(false);
      return;
    }

    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        agb_version: AGB_VERSION,
      });
      toast.success(language === 'de' ? 'Konto erfolgreich erstellt!' : 'Account created successfully!');
      navigate('/onboarding/level-quiz');
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = language === 'de' ? 'Registrierung fehlgeschlagen' : 'Registration failed';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      }
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="card-static p-8 md:p-10" data-testid="register-form">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/logo.png" alt="Padelarena Wädenswil" className="h-20 w-auto mx-auto mb-4" />
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 tracking-tight">Padelarena Wädenswil</h1>
            <p className="text-[10px] text-emerald-600 font-bold tracking-wider uppercase mb-3">Padlr. Booking</p>
            <h2 className="text-lg font-semibold text-slate-700 mb-1">
              {t('registerTitle')}
            </h2>
            <p className="text-slate-500 text-sm">
              {language === 'de' 
                ? 'Erstellen Sie Ihr Konto für Buchungen, Matches und Trainings'
                : 'Create your account for bookings, matches and lessons'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-[#FF3B30]/30 text-red-500 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="label-text">
                {t('name')} *
              </Label>
              <div className="relative">
                <User 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" 
                />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-500/50 focus:border-emerald-500 rounded-lg"
                  placeholder="Max Mustermann"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="label-text">
                {t('email')} *
              </Label>
              <div className="relative">
                <EnvelopeSimple 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" 
                />
                <Input
                  id="email"
                  name="email"
                  type="text"
                  inputMode="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-500/50 focus:border-emerald-500 rounded-lg"
                  placeholder="name@example.com"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="label-text">
                {t('phone')}
              </Label>
              <div className="relative">
                <Phone 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" 
                />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-500/50 focus:border-emerald-500 rounded-lg"
                  placeholder="+41 79 123 45 67"
                  data-testid="register-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="label-text">
                {t('password')} *
              </Label>
              <div className="relative">
                <Lock 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" 
                />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 pr-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-500/50 focus:border-emerald-500 rounded-lg"
                  placeholder="••••••••"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="label-text">
                {language === 'de' ? 'Passwort bestätigen' : 'Confirm Password'} *
              </Label>
              <div className="relative">
                <Lock 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" 
                />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-500/50 focus:border-emerald-500 rounded-lg"
                  placeholder="••••••••"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2" data-testid="agb-checkbox-wrap">
              <Checkbox
                id="agb"
                checked={agbAccepted}
                onCheckedChange={(v) => setAgbAccepted(!!v)}
                className="mt-0.5"
                data-testid="register-agb-checkbox"
              />
              <label htmlFor="agb" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                {language === 'de' ? (
                  <>
                    Ich akzeptiere die{' '}
                    <Link to="/agb" target="_blank" className="text-emerald-700 underline font-semibold">AGB</Link>
                    {' '}und die{' '}
                    <Link to="/privacy" target="_blank" className="text-emerald-700 underline font-semibold">Datenschutzerklärung</Link>.
                    Insbesondere die Stornierungsbedingungen: <strong>Court-Buchungen 24h</strong>, <strong>Mixins/Events 2h</strong> vor Beginn kostenlos stornierbar.
                  </>
                ) : (
                  <>
                    I accept the{' '}
                    <Link to="/terms" target="_blank" className="text-emerald-700 underline font-semibold">Terms</Link>
                    {' '}and the{' '}
                    <Link to="/privacy" target="_blank" className="text-emerald-700 underline font-semibold">Privacy Policy</Link>.
                    In particular the cancellation rules: <strong>court bookings 24h</strong>, <strong>mixins/events 2h</strong> before start (free cancellation).
                  </>
                )}
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading || !agbAccepted}
              className="w-full btn-primary disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('loading')}
                </span>
              ) : (
                t('register')
              )}
            </Button>

            {/* Privacy notice (Swiss revDSG) - now covered by AGB checkbox above */}
            <p className="text-[11px] text-slate-500 leading-relaxed text-center px-2" data-testid="privacy-notice">
              {language === 'de'
                ? <>Deine Daten werden ausschliesslich zur Verwaltung deiner Buchungen genutzt. Details in unserer <Link to="/privacy" className="underline hover:text-slate-700">Datenschutzerklärung</Link>.</>
                : <>Your data is used solely to manage your bookings. See our <Link to="/privacy" className="underline hover:text-slate-700">privacy policy</Link>.</>}
            </p>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm">
              {t('hasAccount')}{' '}
              <Link 
                to="/login" 
                className="text-emerald-600 hover:underline font-medium"
                data-testid="login-link"
              >
                {t('login')}
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
